import React, { useState, useRef } from "react";
import EICacheViewTable from "./eiCacheView";
import { Box, Button, Typography } from "@mui/material";
import { NotificationManager } from "react-notifications";
import "../util/tables.css";
import * as API from "../api/index";
import { useDataView } from "../../contexts/data.view.context";
import AppDialog from "../util/appDialog";
import ViewDetailsIcon from "../util/detailsModal/viewDetailsIcon";
import ShowLoadingBar from "../util/showLoadingBar";
import { useFetchOnMount } from "../../hooks/useFetchOnMount";

function handleClick(name) {
  const resourcePath = "integrations/esb/cache/clear/" + name;

  API.ClearCache(resourcePath).then((response) => {
    if (response.code === 200) {
      NotificationManager.success(response.message, name, 10000);
    } else {
      NotificationManager.error(response.message, "Failed!", 10000);
    }
  });
}

const EICaches = () => {
  const { fetchEICacheList, eiCacheList } = useDataView();

  const [openDetail, setOpenDetail] = useState(false);
  const refEnvCell = useRef(null);

  const handleDetailClickOpen = ({ row }) => {
    refEnvCell.current = row.original;
    setOpenDetail(true);
  };

  const handleDetailClose = () => {
    setOpenDetail(false);
  };

  // Single, page-level renderer — every row's info icon shares this ONE
  // <AppDialog> (rendered once below, outside this per-row cell function)
  // instead of each row mounting its own dialog. With N rows all sharing
  // the same `openDetail` state, N simultaneously-mounted dialogs made
  // clicks land unreliably (this is what "the info icon loads nothing"
  // turned out to be) — same root cause as the Sync dialog bug documented
  // in CONFIG_DIFF_MATCH_SPEC.md.
  function viewDetails() {
    return function ({ cell, row }) {
      const cellValue = cell.getValue();

      return (
        <>
          <ViewDetailsIcon
            row={row}
            tooltip="View Details"
            onClick={handleDetailClickOpen}
          />
          {cellValue}
        </>
      );
    };
  }

  const columns = [
    {
      accessorKey: "Name",
      header: "Name",
      id: "Name",
      size: 100,
      Cell: viewDetails(),
    },
    {
      accessorKey: "Size",
      header: "Size",
      id: "Size",
      size: 100,
    },
    {
      accessorKey: "Action",
      header: "Action",
      size: 75,

      Cell: ({ row }) => {
        return (
          <Button
            variant="contained"
            onClick={() => handleClick(row.original.Name)}
          >
            REFRESH
          </Button>
        );
      },
    },
  ];

  useFetchOnMount(eiCacheList, fetchEICacheList);

  let loading = false;
  if (eiCacheList.length === 0) {
    loading = true;
  } else {
    loading = false;
  }

  if (Boolean(loading)) {
    return <ShowLoadingBar title="LIST of Caches" loading={loading} />;
  }

  return (
    <div className="cache-table">

      {eiCacheList.length > 0 && (
        <EICacheViewTable
          data={eiCacheList.map((cache) => {
            return {
              Name: cache.name,
              Size: cache.size,
              type: cache.type,
              keys: cache.keys,
            };
          })}
          columns={columns}
        />
      )}

      <AppDialog
        open={openDetail}
        onClose={handleDetailClose}
        title={
          refEnvCell.current?.Name
            ? `Cache Contents — ${refEnvCell.current.Name}`
            : "Cache Contents"
        }
        maxWidth="md"
        actions={
          <Button variant="text" color="inherit" onClick={handleDetailClose}>
            Close
          </Button>
        }
      >
        {(() => {
          const raw = refEnvCell.current?.keys || "";
          // Backend returns keys as a Java List.toString()-style string,
          // e.g. "[keyOne, keyTwo, keyThree]" — not JSON, so split by hand.
          const trimmed = raw.replace(/^\[/, "").replace(/\]$/, "").trim();
          const keyList = trimmed ? trimmed.split(", ") : [];

          return (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                {refEnvCell.current?.type}
                {refEnvCell.current?.type ? " • " : ""}
                {keyList.length} key{keyList.length === 1 ? "" : "s"}
              </Typography>

              {keyList.length > 0 ? (
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.5,
                    maxHeight: 420,
                    overflowY: "auto",
                  }}
                >
                  {keyList.map((key, i) => (
                    <Box
                      component="li"
                      key={`${key}-${i}`}
                      sx={{
                        fontSize: "0.8rem",
                        fontFamily: "monospace",
                        color: "text.primary",
                        py: 0.4,
                        wordBreak: "break-all",
                      }}
                    >
                      {key}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No entries found in this cache.
                </Typography>
              )}
            </>
          );
        })()}
      </AppDialog>
    </div>
  );
};

export default EICaches;
